import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { User } from '../models/user';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  finalize,
  map,
  of,
} from 'rxjs';
import { Login } from '../models/login';
import { AuthService } from '../shared/auth/auth.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LoginResponses } from '../models/login-responses';
import { LoaderService } from './loader.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService extends BaseService<User> {
  private userDataSubject = new BehaviorSubject<User>(this.getUser());
  userData$ = this.userDataSubject.asObservable();
  private user: User = new User();
  private loginDetails: Login = new Login();
  personalDetails: User = new User();
  private subscription: Subscription;
  public profilePictureUrlSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  private searchLocationSubject = new BehaviorSubject<
    {
      address: string;
      latitude: number;
      longitude: number;
      addressComponent?: google.maps.GeocoderAddressComponent[];
      name?: string;
    }[]
  >([]);
  searchLocation$ = this.searchLocationSubject.asObservable();
  private locationDataSubject = new BehaviorSubject<{
    address: string; latitude: number;
    longitude: number;
  }[]>([]);
  locationData$ = this.locationDataSubject.asObservable();
  readonly allowedCountries = environment.registrationAllowedCountries;

  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private loaderService: LoaderService
  ) {
    super(httpClient);
  }

  updateUser(userData: User) {
    this.user = userData;
    localStorage.setItem('userData', JSON.stringify(userData));
    this.userDataSubject.next(userData);
  }

  getUser(): User {
    const userData = localStorage.getItem('userData');
    if (this.user && this.user.email == "" && userData) {
      this.user = JSON.parse(userData);
    }

    return this.user;
  }

  setProfilePictureUrl(url: any): void {
    localStorage.setItem('ProfileUrl', url);
    this.profilePictureUrlSubject.next(url);
  }

  getProfilePictureUrl(): Observable<string> {
    return this.profilePictureUrlSubject.asObservable();
  }

  getProfilePicture(image: any): Observable<any> {
    return this.getUserImage(`/api/tenants/me/retriveImage?name=`, image).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  loggedInUser(email: string, password: string) {
    this.loginDetails.password = password;
    this.loginDetails.email = email;

    this.subscription = this.authService
      .login(this.loginDetails)
      .subscribe((response: LoginResponses) => {
        if (response && response.access_token) {
          localStorage.setItem('token', response.access_token);
          localStorage.setItem('refreshToken', response.refresh_token);
          localStorage.setItem('expiresIn', response.expires_in);
          this.router
            .navigateByUrl('', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['']);
            });
        } else {
          this.toastr.error('Something Went Wrong!');
        }
      });
  }

  completeRegistration(user: User) {
    if (!environment.registrationApprovalRequired) {
      this.loggedInUser(user.email, user.password);
    }
  }

  getPersonalDetails(): Observable<any> {
    return this.getById(`/api/tenants/myProfile`).pipe(
      map((response: any) => {
        this.personalDetails.firstName = response.user.firstname;
        this.personalDetails.lastName = response.user.lastname;
        this.personalDetails.email = response.user.email;
        this.personalDetails.address = response.address1;
        this.personalDetails.city = response.city;
        this.personalDetails.zip = response.zip;
        this.personalDetails.mobileNumber = response.phoneNumber;
        this.personalDetails.instagramId = response.instagramId;
        this.personalDetails.tikTokId = response.tikTokId;
        this.personalDetails.faceBookId = response.facebookId;
        this.personalDetails.profilePicture = response.user.photo;
        this.personalDetails.isMembershipVendor = response.isMembershipVendor;
        this.personalDetails.allowArticleCreation = response.allowArticleCreation;
        this.personalDetails.allowPreRegistration = response.allowPreRegistration;

        return this.personalDetails;
      })
    );
  }

  setSearchLocation(
    address: string,
    latitude: number,
    longitude: number,
    addressComponent?: google.maps.GeocoderAddressComponent[],
    name?: string
  ) {
    const updatedLocations = [
      { address, latitude, longitude, addressComponent, name },
    ];
    this.searchLocationSubject.next(updatedLocations);
  }

  setProfileSelectedLocation(address: string, latitude: number, longitude: number) {
    const updatedLocations = [
      { address, latitude, longitude },
    ];
    this.locationDataSubject.next(updatedLocations);
  }

  getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position);
          },
          (error) => {
            reject(error);
          }
        );
      } else {
        reject('Geolocation is not supported by this browser.');
      }
    });
  }

  getCurrentLocation(): Promise<{ city: string, lat: number, lng: number }> {
    return this.getCurrentPosition().then(
      (position) => {
        return this.getLocationName(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.error('Error getting location', error);
        throw error;
      }
    );
  }

  getLocationName(lat: number, lng: number): Promise<{ city: string, lat: number, lng: number }> {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      const latlng = new google.maps.LatLng(lat, lng);

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0]) {
            for (let component of results[0].address_components) {
              if (component.types.includes('locality')) {
                const city = component.long_name;
                resolve({ city, lat, lng });
                return;
              }
            }
            reject('Locality not found in address components');
          } else {
            reject('No results found');
          }
        } else {
          reject('Geocoder failed due to: ' + status);
        }
      });
    });
  }

  async getUserCountry(): Promise<boolean> {
    if (!environment.registrationGeoRestrictionEnabled) {
      return true;
    }

    try {
      const country = await this.getCurrentPosition().then(
        (position) => {
          const { latitude, longitude } = position.coords;
          return this.getCountryFromGoogleMaps(latitude, longitude);
        },
        () => this.getCountryByIP()
      );

      return this.allowedCountries.includes(country);
    } catch {
      // Live registration is intentionally fail-closed when neither browser nor IP
      // geolocation can establish that the user is in an allowed country.
      return false;
    }
  }

  getCountryFromGoogleMaps(lat: number, lng: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      const latlng = new google.maps.LatLng(lat, lng);
      geocoder.geocode({ location: latlng }, (results: any[], status: string) => {
        if (status === google.maps.GeocoderStatus.OK) {
          for (const result of results) {
            const countryComponent = result.address_components.find((comp: any) =>
              comp.types.includes('country')
            );
            if (countryComponent) {
              resolve(countryComponent.long_name);
              return;
            }
          }
          reject('Country not found');
        } else {
          reject('Geocoder failed due to: ' + status);
        }
      });
    });
  }

  async getCountryByIP(): Promise<string> {
    const res = await fetch('https://ipapi.co/json');
    const data = await res.json();
    return data.country_name;
  }

  async getLocationByIP(): Promise<any> {
    const res = await fetch('https://ipapi.co/json');
    const data = await res.json();
    return { lat: data.latitude, lng: data.longitude };
  }

  async getCityByIP(): Promise<any> {
    const res = await fetch('https://ipapi.co/json');
    const data = await res.json();
    return data.city;
  }

  getCurrentLatLng(): any {
    return this.getCurrentPosition().then(
      (position) => {
        return { lat: position.coords.latitude, lng: position.coords.longitude }
      },
      (error) => {
        return this.getLocationByIP();
      }
    );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private foodieOfferList = [
    {
      id: 1,
      title: "Thursday Salad",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 2,
      title: "Pasta Special",
      type: "offer",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 3,
      title: "Cocktails",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 4,
      title: "Thursday Salad",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 5,
      title: "Pasta Special",
      type: "offer",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 6,
      title: "Cocktails",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 7,
      title: "Thursday Salad",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 8,
      title: "Pasta Special",
      type: "offer",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    },
    {
      id: 9,
      title: "Cocktails",
      type: "deal",
      description: "Thursday special deal salad $2.9",
      time: "9 AM - 11 PM"
    }
  ];

  private foodieNewsList = [
    {
      id: 1,
      title: "I don't need to drink",
      tags: ["Drinks"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-1.jpg"
    },
    {
      id: 2,
      title: "Bow ties are cool!",
      tags: ["Drinks", "Breakfast"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-2.jpg"
    },
    {
      id: 3,
      title: "Yo Yogurt!",
      tags: ["Drinks", "Breakfast", "Healthy"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-3.jpg"
    },
    {
      id: 4,
      title: "Some good news here",
      tags: ["Drinks", "Healthy"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-4.jpg"
    },
    {
      id: 5,
      title: "Bow ties are cool!",
      tags: ["Healthy"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-1.jpg"
    },
    {
      id: 6,
      title: "Yo Yogurt!",
      tags: ["Drinks", "Healthy"],
      date: "2024-04-24T10:00:15.553Z",
      imagePath: "./assets/images/food-news-2.jpg"
    }
  ];

  getFoodieNewsData(): Observable<any> {
    return of(this.foodieNewsList);
  }

  getFoodieOffersData(): Observable<any> {
    return of(this.foodieOfferList);
  }
}
